/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  ComponentType,
  lazy,
  Suspense,
  useEffect,
  useState,
} from "react";
import useScrollHandler from "../hooks/useScrollHandler";
import ClientOnly from "./ClientOnly";

export const NullLoader = () => null;

// TODO: Improve the types here to remove `any`. I couldn't find a way to get
// generics to work with the `dynamicImports` array. Maybe we can remove the
// array as we never pass more than one import to a single LazyLoader anyway.
type Props<T extends ComponentType<any>> = {
  delay?: number;
  dynamicImport(): Promise<{ default: T }>;
  LoadingComponent?(): JSX.Element | null;
  props?: any;
};

export default function LazyLoader<T extends ComponentType<any>>({
  delay = 7000,
  dynamicImport,
  LoadingComponent = NullLoader,
  props,
}: Props<T>) {
  const [loaded, setLoaded] = useState(false);
  const [hasScrolled, removeEventListenerRef] = useScrollHandler();

  useEffect(() => {
    const handleLoad = () => {
      setLoaded(true);
      removeEventListenerRef.current?.();
    };

    let timeout: NodeJS.Timeout;
    if (hasScrolled) {
      handleLoad();
    } else {
      timeout = setTimeout(handleLoad, delay);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [delay, hasScrolled, removeEventListenerRef]);

  if (!loaded) {
    return <LoadingComponent />;
  }

  const LazyComponent = lazy(dynamicImport);
  return (
    <ClientOnly>
      <Suspense fallback={<LoadingComponent />}>
        <LazyComponent {...props} />
      </Suspense>
    </ClientOnly>
  );
}
